import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputComponent } from '../input/input.component';
import { Customer } from '../../models/Customer.model';
import { applyPricingMode, composeShopHours, isFlatMode, parseMoney, PricingMode } from './work-item-form';

/**
 * The work-item form fields shared by the work-item edit modal and the
 * create-quote wizard: title, mechanic, miles, the pricing section
 * (computed vs flat-fee with a live summary), and notes. The host owns the
 * FormGroup — built with buildWorkItemFormControls() — plus any host-specific
 * controls and surrounding UI (vehicle select, images, rejection handling).
 */
@Component({
  selector: 'app-work-item-form-fields',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent],
  templateUrl: './work-item-form-fields.component.html',
  styleUrls: ['./work-item-form-fields.component.scss']
})
export class WorkItemFormFieldsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  // For the mechanic dropdown and the labor line of the live summary.
  @Input() mechanics: Customer[] = [];

  private pricingModeSub?: Subscription;

  ngOnInit(): void {
    // The host's ngOnInit runs first (parents init before children), so any
    // edit-mode preset of pricingMode has already been patched in.
    applyPricingMode(this.form, this.form.get('pricingMode')?.value ?? 'computed');
    this.pricingModeSub = this.form.get('pricingMode')!.valueChanges
      .subscribe((mode: PricingMode) => applyPricingMode(this.form, mode));
  }

  ngOnDestroy(): void {
    this.pricingModeSub?.unsubscribe();
  }

  get isFlatMode(): boolean {
    return isFlatMode(this.form);
  }

  getMechanicDisplayName(mechanic: Customer): string {
    return `${mechanic.firstName} ${mechanic.lastName} (${mechanic.email})`;
  }

  // ============= LIVE PRICING SUMMARY =============

  private get selectedMechanicRate(): number {
    const empUserId = this.form.get('empUserId')?.value;
    return this.mechanics.find(m => m.userId === empUserId)?.hourlyRate || 0;
  }

  get summaryLabor(): number {
    return (composeShopHours(this.form) ?? 0) * this.selectedMechanicRate;
  }

  get summaryParts(): number {
    return parseMoney(this.form.get('partsPricing')?.value) ?? 0;
  }

  get summaryFlatFee(): number {
    return parseMoney(this.form.get('flatFee')?.value) ?? 0;
  }

  get summaryTotal(): number {
    return this.isFlatMode ? this.summaryFlatFee : this.summaryLabor + this.summaryParts;
  }

  // Qualitative — the exact rate lives server-side (TAX_RATE).
  get summaryTaxNote(): string {
    if (this.isFlatMode) {
      return this.form.get('flatFeeTaxable')?.value === true
        ? 'Sales tax applies to the full flat fee'
        : 'No sales tax';
    }
    return this.summaryParts > 0 ? 'Sales tax applies to parts' : 'No sales tax';
  }

  /** Hint when shop time is entered but labor can't be priced yet. */
  get laborNeedsMechanic(): boolean {
    return !this.isFlatMode && (composeShopHours(this.form) ?? 0) > 0 && this.selectedMechanicRate === 0;
  }
}
